/**
 * Vocabulário compartilhado pelas abas Painel e Ligações da tela /ura.
 *
 * O editor do fluxo (pages/Ura.js) tem o VISUAL dele, que serve para desenhar
 * caixa. Aqui o assunto é histórico: o que importa é ler rápido o que
 * aconteceu com a ligação.
 */
import {
    FaVolumeUp, FaListUl, FaHeadset, FaPhoneAlt, FaUserTie, FaRobot,
    FaCodeBranch, FaSignOutAlt, FaPhoneSlash,
} from 'react-icons/fa';

export const ACCENT = '#F68633';

/** Em que a ligação deu. É o eixo do painel inteiro. */
export const DESFECHOS = {
    humano: { label: 'Falou com alguém da equipe', curto: 'Equipe', cor: 'green' },
    bot: { label: 'Atendente virtual', curto: 'Virtual', cor: 'teal' },
    ura_resolveu: { label: 'Ouviu o recado e desligou', curto: 'Ouviu o recado', cor: 'blue' },
    abandonou: { label: 'Desligou no meio', curto: 'Desligou', cor: 'red' },
    sem_atendimento: { label: 'Ninguém pôde atender', curto: 'Sem atendimento', cor: 'pink' },
    transbordo: { label: 'Devolvida para a operadora', curto: 'Operadora', cor: 'orange' },
    falha_tecnica: { label: 'Falha técnica', curto: 'Falhou', cor: 'yellow' },
    sem_saida: { label: 'O desenho deixou sem saída', curto: 'Sem saída', cor: 'purple' },
    // A ligação ainda não terminou: o desfecho de verdade só existe quando ela
    // desliga. Antes isso caía em "sem histórico" e parecia falha.
    em_aberto: { label: 'Ainda na linha', curto: 'Na linha', cor: 'blue' },
    sem_ura: { label: 'Sem histórico de caminho', curto: 'Sem histórico', cor: 'gray' },
};

export const desfechoDe = (valor) => DESFECHOS[valor] || DESFECHOS.sem_ura;

/** Cada tipo de caixa, com o mesmo ícone e a mesma cor do editor. */
export const VISUAL_NO = {
    audio: { icone: FaVolumeUp, cor: 'orange', rotulo: 'Áudio' },
    menu: { icone: FaListUl, cor: 'orange', rotulo: 'Menu' },
    ramais: { icone: FaHeadset, cor: 'green', rotulo: 'Vendedores' },
    ramal: { icone: FaPhoneAlt, cor: 'green', rotulo: 'Ramal' },
    gerente: { icone: FaUserTie, cor: 'purple', rotulo: 'Gerente de contas' },
    bot: { icone: FaRobot, cor: 'teal', rotulo: 'Atendente virtual' },
    condicao: { icone: FaCodeBranch, cor: 'blue', rotulo: 'Condição' },
    encerrar: { icone: FaSignOutAlt, cor: 'gray', rotulo: 'Encerrar' },
    transbordo: { icone: FaPhoneSlash, cor: 'red', rotulo: 'Operadora' },
};

export const visualDoNo = (tipo) => VISUAL_NO[tipo] || { icone: FaCodeBranch, cor: 'gray', rotulo: tipo || '?' };

/** O nome de cada saída na boca do povo (mesmo vocabulário do editor). */
export const ROTULO_SAIDA = {
    fim: 'depois',
    ninguem: 'ninguém atendeu',
    sem_gerente: 'não tem gerente',
    sem_resposta: 'não digitou',
    invalido: 'opção errada',
    sim: 'sim',
    nao: 'não',
    transferir: 'pediu vendedor',
    inicio: 'começo',
    digito: 'digitou',
    atendeu: 'atendeu',
    desligou: 'desligou',
    encerrou: 'encerrou',
    recomeco: 'o fluxo mudou no meio',
    emergencia: 'emergência',
};

export const rotuloSaida = (evento) => {
    if (!evento) return '';
    return evento.startsWith('digito_') ? `digitou ${evento.slice(7)}` : (ROTULO_SAIDA[evento] || evento);
};

/** As condições, escritas como a pessoa lê (o painel não tem a lista do backend). */
export const ROTULO_CONDICAO = {
    horario: 'dentro do horário comercial',
    tem_gerente: 'tem gerente de contas',
    cliente_conhecido: 'é cliente cadastrado',
    alguem_conectado: 'tem vendedor conectado',
    bot_disponivel: 'atendente virtual de pé',
};

/* ------------------------------------------------------------------ */

export const fmtSeg = (s) => {
    if (s == null) return '—';
    const n = Math.max(0, Math.round(s));
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60);
    const r = n % 60;
    return r ? `${m}min ${r}s` : `${m}min`;
};

export const fmtMs = (ms) => {
    if (ms == null) return '—';
    return ms < 1000 ? `${ms}ms` : fmtSeg(ms / 1000);
};

export const fmtNumero = (raw) => {
    const n = String(raw || '').replace(/\D/g, '');
    if (n.length === 13 && n.startsWith('55')) return fmtNumero(n.slice(2));
    if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
    if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
    return raw || '—';
};

export const fmtDataHora = (iso) => {
    if (!iso) return '—';
    try {
        // Com o ano: o filtro aceita qualquer período, e "12/03 09:15" sozinho
        // não diz de que ano é.
        return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch (_) { return '—'; }
};

export const fmtHoraMin = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (_) { return ''; }
};

/** '2026-09-17' -> '17/09' (sem passar por Date, que rouba um dia no fuso). */
export const fmtDiaCurto = (dia) => {
    const p = String(dia || '').split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}` : dia;
};

export const isoDoDia = (d) => {
    const z = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

export const diasAtras = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return isoDoDia(d);
};

export const hojeISO = () => isoDoDia(new Date());

export const pct = (parte, total) => (total > 0 ? Math.round(((Number(parte) || 0) / total) * 100) : 0);
